/**
 * /sandbox/render-gallery — the saved estimate page's render stage on its own,
 * driven by a scripted status sequence (queued → rendering, pictures landing
 * one by one → the 360) so it can be styled and screenshotted with no worker.
 * With ?guid=<a saved estimate's guid> it polls the real job instead.
 */

import React, { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { FloorPlan } from '../calculations/plan/floorPlan';
import type { Surface } from '../types/sections';
import { RenderStage } from '../components/results/render/RenderStage';
import type { RenderStatusFetcher } from '../components/results/render/useRenderStatus';
import { PICTURE_ORDER, type PictureId, type RenderStatus } from '../components/results/render/types';
import '../components/results/ResultsVisualCard.css';

const SAMPLE = '/images/visualizer/planner-estimate-sample-09-2024.jpg';

const wall = (id: string, width: number, height: number, openings: Surface['openings'] = []): Surface =>
  ({ id, kind: 'wall', name: id, width, height, sections: [], openings } as unknown as Surface);

const PLAN: FloorPlan = {
  rooms: [{
    id: 'r1', name: 'Garage', x: 0, z: 0, length: 20, width: 12, height: 10,
    surfaces: {
      walls: [
        wall('front', 20, 10, [{ id: 'gd', kind: 'garageDoor', x: 6, y: 0, width: 8, height: 7 }]),
        wall('right', 12, 10, [{ id: 'win', kind: 'window', x: 5, y: 4, width: 2, height: 3 }]),
        wall('back', 20, 10, [{ id: 'win2', kind: 'window', x: 3, y: 4, width: 3, height: 3 }]),
        wall('left', 12, 10, [{ id: 'door', kind: 'door', x: 1, y: 0, width: 3, height: 7 }]),
      ],
    },
  }],
  openWalls: [],
};

/** The scripted sequence: one status per poll (3 s apart). */
function script(): RenderStatus[] {
  const base = (job: RenderStatus['job'], doneIds: PictureId[]): RenderStatus => ({
    guid: 'sandbox',
    job,
    pictures: PICTURE_ORDER.map((id) => ({
      id, name: id, done: doneIds.includes(id),
      url: doneIds.includes(id) ? SAMPLE : null, thumbUrl: doneIds.includes(id) ? SAMPLE : null,
      width: id === '360' ? 4096 : 1920, height: id === '360' ? 2048 : 1080,
    })),
    preview: SAMPLE,
    panorama: { position: [0, 5.5, 0], target: [0, 5.5, -5], face: 'front' },
    pollMs: 3000,
  });
  const job = (over: Partial<NonNullable<RenderStatus['job']>>): RenderStatus['job'] => ({
    id: 1, version: 1, status: 'queued', queuePosition: 3, queueAhead: 2, percent: 0, picture: null, stage: null,
    etaSec: 900, attempts: 0, error: null, createdAt: new Date().toISOString(), startedAt: null, finishedAt: null, ...over,
  });
  const pic = (id: PictureId, index: number, percent: number) => ({ id, index, count: 6, name: id, percent });
  const out: RenderStatus[] = [
    base(job({ status: 'queued', queuePosition: 3, queueAhead: 2 }), []),
    base(job({ status: 'queued', queuePosition: 1, queueAhead: 0, etaSec: 300 }), []),
    base(job({ status: 'claimed', queuePosition: 0, queueAhead: 0, etaSec: 300 }), []),
    base(job({ status: 'rendering', stage: 'rendering', picture: pic('user', 1, 20), percent: 3, etaSec: 280 }), []),
    base(job({ status: 'rendering', stage: 'rendering', picture: pic('user', 1, 70), percent: 11, etaSec: 250 }), []),
  ];
  const done: PictureId[] = [];
  PICTURE_ORDER.forEach((id, i) => {
    done.push(id);
    const next = PICTURE_ORDER[i + 1];
    const percent = Math.round(((i + 1) / 6) * 100);
    if (next) {
      out.push(base(job({ status: 'rendering', stage: 'rendering', picture: pic(next, i + 2, 15), percent: Math.min(99, percent + 2), etaSec: (5 - i) * 45 }), [...done]));
      out.push(base(job({ status: 'rendering', stage: 'rendering', picture: pic(next, i + 2, 65), percent: Math.min(99, percent + 10), etaSec: (5 - i) * 40 }), [...done]));
    }
  });
  out.push(base(job({ status: 'done', stage: 'done', percent: 100, etaSec: 0, finishedAt: new Date().toISOString() }), [...PICTURE_ORDER]));
  out[out.length - 1].pollMs = 0;
  return out;
}

export const RenderGallerySandbox: React.FC = () => {
  const [params] = useSearchParams();
  const guid = params.get('guid');
  const [step, setStep] = useState(0);
  const [strip, setStrip] = useState<HTMLDivElement | null>(null);
  const steps = useMemo(script, []);
  const fetcher: RenderStatusFetcher | undefined = useMemo(() => {
    if (guid) return undefined;
    let i = 0;
    return async () => { const s = steps[Math.min(i, steps.length - 1)]; i += 1; setStep(Math.min(i, steps.length)); return s; };
  }, [guid, steps]);

  return (
    <div className="container py-4">
      <h1 className="h4 mb-1">Render gallery sandbox</h1>
      <p className="text-muted small mb-3">
        {guid
          ? <>Polling the real job of estimate <code>{guid}</code>.</>
          : <>A scripted job: step {step} of {steps.length}. Add <code>?guid=…</code> to watch a real one.</>}
      </p>
      <div className="results-visual-card position-relative overflow-hidden rounded-4" style={{ ['--rvc-overlay-h' as string]: '96px' }}>
        <div className="rvc-image-stage">
          <RenderStage guid={guid ?? 'sandbox'} plan={PLAN} focusRoomId="r1" alt="Your room" fetcher={fetcher} dockOutlet={strip} />
        </div>
        <div className="rvc-render-strip" ref={setStrip} />
        <div className="results-visual-overlay" style={{ height: 96, display: 'flex', alignItems: 'center', padding: '0 24px', color: '#fff', lineHeight: 'normal' }}>
          (the frosted strip: price and configuration)
        </div>
      </div>
    </div>
  );
};
